import * as React from "react";
import {
  ArrowUpRight,
  Clock,
  Euro,
  LayoutTemplate,
  MinusCircle,
  Sparkles,
  UserRound,
} from "lucide-react";
import { ParallelogramButton } from "@/components/ParallelogramButton";
import { StarField } from "@/components/StarField";
import { useInView } from "@/hooks/useInView";

const COLUMNS = [
  {
    id: "agentur",
    label: "Klassische Agentur",
    title: "Direkt 3.000 € oder mehr investieren?",
    meter: { icon: Euro, value: "3.000 €+", caption: "einmalig, vorab", fill: 92 },
    accent: 12, // warmes Rot-Orange
    text: "Eine professionelle Webseite ist wichtig. Eine hohe Einmalzahlung ist für viele kleinere Unternehmen trotzdem eine unnötige Belastung. Hinzu kommen häufig laufende Kosten für Hosting, Wartung und spätere Anpassungen.",
    points: [
      "hohe Investition zum Projektstart",
      "Änderungen werden häufig zusätzlich berechnet",
      "Wartung und Hosting nicht immer enthalten",
      "lange Abstimmungs- und Angebotsprozesse",
      "Kapital wird gebunden, bevor die Webseite Ergebnisse liefert",
    ],
    conclusion:
      "Für große Sonderprojekte kann das sinnvoll sein. Für eine professionelle Unternehmenswebseite geht es heute aber auch planbarer.",
  },
  {
    id: "baukasten",
    label: "Baukasten",
    title: "Oder alles selbst im Baukasten versuchen?",
    meter: {
      icon: Clock,
      value: "40+ Stunden",
      caption: "eigene Arbeitszeit",
      fill: 78,
    },
    accent: 42, // warmes Gelb
    text: "Wix, IONOS und andere Baukästen wirken zunächst einfach. Doch eine professionelle Webseite besteht nicht nur daraus, ein paar Texte und Bilder in eine Vorlage zu ziehen.",
    points: [
      "monatliche Kosten für Baukasten und Zusatzfunktionen",
      "viele Stunden eigene Arbeit",
      "Design wirkt schnell austauschbar",
      "mobile Darstellung muss selbst geprüft werden",
      "SEO, Technik und Formulare müssen eingerichtet werden",
      "bei Problemen fehlt ein persönlicher Ansprechpartner",
      "Zeit und Nerven fehlen anschließend im eigenen Unternehmen",
    ],
    conclusion:
      "Ein Baukasten spart nur dann Geld, wenn deine eigene Zeit nichts kostet.",
  },
];

const SOLUTION_POINTS = [
  { icon: LayoutTemplate, label: "Individuelles Design" },
  { icon: UserRound, label: "Persönliche Umsetzung" },
  { icon: Sparkles, label: "Keine Einmalzahlung" },
];

const CompareCard = ({ column, side }) => {
  const [ref, inView] = useInView({ threshold: 0.15 });
  const MeterIcon = column.meter.icon;
  const accent = column.accent;

  return (
    <article
      ref={ref}
      className={`group relative flex h-full flex-col overflow-hidden border border-border/70 bg-[hsl(230_26%_6%/0.72)] p-8 backdrop-blur-sm transition-[opacity,transform,border-color,box-shadow] duration-[900ms] ease-out sm:p-10 ${
        inView
          ? "translate-x-0 translate-y-0 opacity-100"
          : `${side === "left" ? "-translate-x-8" : "translate-x-8"} translate-y-6 opacity-0`
      } hover:-translate-y-1.5`}
      style={{
        boxShadow: inView ? undefined : "none",
      }}
    >
      {/* Akzentkante + Glow beim Hover */}
      <span
        aria-hidden
        className="absolute inset-x-0 top-0 h-[2px] origin-left scale-x-0 transition-transform duration-700 ease-out group-hover:scale-x-100"
        style={{
          background: `linear-gradient(90deg, hsl(${accent} 85% 58% / 0.9), transparent)`,
        }}
      />
      <span
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-64 w-64 rounded-full opacity-0 blur-3xl transition-opacity duration-700 group-hover:opacity-100"
        style={{
          background: `radial-gradient(circle, hsl(${accent} 85% 55% / 0.18), transparent 70%)`,
        }}
      />

      <p
        className="relative font-display text-[11px] font-light uppercase tracking-[0.3em]"
        style={{ color: `hsl(${accent} 70% 68% / 0.9)` }}
      >
        {column.label}
      </p>

      <h3 className="relative mt-5 font-display text-xl font-light leading-snug tracking-tight text-foreground sm:text-2xl">
        {column.title}
      </h3>

      {/* Kennzahl mit Füllbalken */}
      <div className="relative mt-7 border border-border/60 bg-[hsl(230_24%_8%/0.7)] p-5">
        <div className="flex items-end justify-between gap-4">
          <div className="flex items-center gap-3">
            <span
              className="flex h-9 w-9 items-center justify-center border"
              style={{
                borderColor: `hsl(${accent} 70% 55% / 0.4)`,
                background: `hsl(${accent} 70% 50% / 0.12)`,
              }}
            >
              <MeterIcon
                className="h-4 w-4"
                strokeWidth={1.6}
                style={{ color: `hsl(${accent} 85% 68%)` }}
              />
            </span>
            <span className="font-display text-2xl font-extralight leading-none tracking-tight text-foreground">
              {column.meter.value}
            </span>
          </div>
          <span className="pb-1 text-[11px] font-light uppercase tracking-[0.16em] text-muted-foreground">
            {column.meter.caption}
          </span>
        </div>

        <div className="mt-4 h-1 w-full overflow-hidden bg-[hsl(230_20%_14%)]">
          <span
            className="block h-full transition-[width] duration-[1400ms] ease-out"
            style={{
              width: inView ? `${column.meter.fill}%` : "0%",
              background: `linear-gradient(90deg, hsl(${accent} 80% 45%), hsl(${accent} 90% 62%))`,
            }}
          />
        </div>
      </div>

      <p className="relative mt-6 text-sm font-light leading-relaxed text-muted-foreground">
        {column.text}
      </p>

      <ul className="relative mt-7 flex flex-col gap-3 border-t border-border/60 pt-7">
        {column.points.map((point, index) => (
          <li
            key={point}
            className={`flex items-start gap-3 transition-[opacity,transform] duration-500 ease-out ${
              inView ? "translate-x-0 opacity-100" : "-translate-x-3 opacity-0"
            }`}
            style={{ transitionDelay: `${240 + index * 90}ms` }}
          >
            <MinusCircle
              className="mt-0.5 h-4 w-4 shrink-0"
              strokeWidth={1.6}
              style={{ color: `hsl(${accent} 75% 62% / 0.85)` }}
            />
            <span className="text-sm font-light leading-relaxed text-muted-foreground/90">
              {point}
            </span>
          </li>
        ))}
      </ul>

      <p className="relative mt-auto pt-8 font-display text-sm font-light italic leading-relaxed text-foreground/75">
        „{column.conclusion}“
      </p>
    </article>
  );
};

/** Zusammenlaufende Linien von beiden Karten zur Lösung. */
const ConvergingLines = ({ inView }) => (
  <svg
    aria-hidden
    viewBox="0 0 1000 120"
    preserveAspectRatio="none"
    className="pointer-events-none hidden h-[80px] w-full lg:block"
  >
    <defs>
      <linearGradient id="convLeft" x1="0" y1="0" x2="1" y2="1">
        <stop offset="0%" stopColor="hsl(12 80% 60% / 0.15)" />
        <stop offset="100%" stopColor="hsl(222 100% 65% / 0.8)" />
      </linearGradient>
      <linearGradient id="convRight" x1="1" y1="0" x2="0" y2="1">
        <stop offset="0%" stopColor="hsl(42 85% 60% / 0.15)" />
        <stop offset="100%" stopColor="hsl(222 100% 65% / 0.8)" />
      </linearGradient>
    </defs>

    {[
      { d: "M250 0 C 250 70, 500 40, 500 118", stroke: "url(#convLeft)" },
      { d: "M750 0 C 750 70, 500 40, 500 118", stroke: "url(#convRight)" },
    ].map((line) => (
      <path
        key={line.d}
        d={line.d}
        fill="none"
        stroke={line.stroke}
        strokeWidth="1.5"
        strokeDasharray="340"
        strokeDashoffset={inView ? 0 : 340}
        style={{ transition: "stroke-dashoffset 1400ms ease-out 300ms" }}
      />
    ))}
  </svg>
);

export const WhyOurModel = () => {
  const [headRef, headInView] = useInView({ threshold: 0.2 });
  const [solutionRef, solutionInView] = useInView({ threshold: 0.18 });

  return (
    <section
      id="vergleich"
      className="grain relative isolate overflow-hidden border-t border-border/60 bg-[hsl(0_0%_0%)] py-24 sm:py-28 lg:py-32"
    >
      <StarField count={40} className="-z-20 opacity-45" />
      <div
        aria-hidden
        className="absolute left-1/2 top-[-12%] -z-20 h-[520px] w-[880px] -translate-x-1/2 rounded-full bg-[radial-gradient(closest-side,hsl(230_90%_50%/0.14),transparent)] blur-3xl"
      />

      <div className="mx-auto max-w-[1500px] px-5 sm:px-8 lg:px-12">
        {/* Kopf */}
        <div
          ref={headRef}
          className="flex flex-col gap-8 lg:flex-row lg:items-end lg:justify-between"
        >
          <div
            className={`max-w-3xl transition-[opacity,transform] duration-700 ease-out ${
              headInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            <p className="font-display text-[11px] font-light uppercase tracking-[0.32em] text-primary-glow">
              Der Vergleich
            </p>
            <h2 className="mt-6 font-display text-3xl font-light leading-[1.12] tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Warum kompliziert,
              <br className="hidden sm:block" /> wenn es auch planbar geht?
            </h2>
            <div className="mt-7 h-px w-32 bg-gradient-hairline" aria-hidden />
          </div>

          <p
            className={`max-w-md text-sm font-light leading-relaxed text-muted-foreground transition-[opacity,transform] delay-150 duration-700 ease-out sm:text-base ${
              headInView ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0"
            }`}
          >
            Warum direkt mehrere Tausend Euro investieren, wenn dein Unternehmen
            diese Komplexität noch gar nicht benötigt? Die beiden üblichen Wege
            im direkten Vergleich.
          </p>
        </div>

        {/* Zwei Spalten */}
        <div className="relative mt-14 grid grid-cols-1 items-stretch gap-6 lg:mt-16 lg:grid-cols-2 lg:gap-16">
          <CompareCard column={COLUMNS[0]} side="left" />
          <CompareCard column={COLUMNS[1]} side="right" />

          {/* Mittelachse mit VS */}
          <div
            aria-hidden
            className="pointer-events-none absolute inset-y-0 left-1/2 hidden -translate-x-1/2 flex-col items-center lg:flex"
          >
            <span className="h-full w-px bg-[linear-gradient(180deg,transparent,hsl(0_0%_100%/0.14),transparent)]" />
            <span className="absolute top-1/2 flex h-14 w-14 -translate-y-1/2 items-center justify-center rounded-full border border-border/70 bg-[hsl(0_0%_0%)] font-display text-xs font-light uppercase tracking-[0.2em] text-muted-foreground shadow-[0_0_40px_hsl(222_100%_60%/0.18)]">
              vs
              <span className="absolute inset-0 animate-ping rounded-full border border-primary/25" />
            </span>
          </div>
        </div>

        <ConvergingLines inView={solutionInView} />

        {/* Lösung */}
        <div
          ref={solutionRef}
          className={`group relative mt-8 overflow-hidden border border-primary/35 bg-[hsl(228_42%_8%/0.85)] p-8 backdrop-blur-sm transition-[opacity,transform] duration-[900ms] ease-out sm:p-10 lg:mt-0 ${
            solutionInView
              ? "translate-y-0 scale-100 opacity-100"
              : "translate-y-10 scale-[0.98] opacity-0"
          }`}
        >
          <span aria-hidden className="absolute inset-x-0 top-0 h-px bg-gradient-hairline" />
          <span
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[radial-gradient(circle,hsl(222_100%_60%/0.24),transparent_70%)] blur-2xl"
          />
          <span
            aria-hidden
            className="pointer-events-none absolute -left-20 bottom-[-40%] h-64 w-64 rounded-full bg-[radial-gradient(circle,hsl(265_90%_62%/0.18),transparent_70%)] blur-2xl"
          />

          <div className="relative flex flex-col gap-10 lg:flex-row lg:items-center lg:justify-between">
            <div className="max-w-2xl">
              <span className="inline-flex items-center gap-2 border border-primary/40 bg-primary/10 px-3 py-1 font-display text-[10px] font-light uppercase tracking-[0.26em] text-primary-glow">
                <Sparkles className="h-3 w-3" strokeWidth={1.8} />
                Unser Weg
              </span>

              <h3 className="mt-6 font-display text-2xl font-light leading-snug tracking-tight text-foreground sm:text-3xl">
                Vision&nbsp;One verbindet beide Welten.
              </h3>

              <p className="mt-4 max-w-xl text-sm font-light leading-relaxed text-muted-foreground sm:text-base">
                Individuelles Design und persönliche Umsetzung – ohne hohe
                Einmalzahlung und ohne dass du deine Webseite selbst bauen musst.
              </p>

              <ul className="mt-7 flex flex-wrap gap-x-7 gap-y-3">
                {SOLUTION_POINTS.map(({ icon: Icon, label }, index) => (
                  <li
                    key={label}
                    className={`flex items-center gap-2.5 transition-[opacity,transform] duration-700 ease-out ${
                      solutionInView
                        ? "translate-y-0 opacity-100"
                        : "translate-y-3 opacity-0"
                    }`}
                    style={{ transitionDelay: `${500 + index * 120}ms` }}
                  >
                    <Icon className="h-4 w-4 text-primary-glow" strokeWidth={1.5} />
                    <span className="text-sm font-light tracking-wide text-muted-foreground">
                      {label}
                    </span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="flex shrink-0 flex-col gap-5 lg:items-end">
              <div className="flex items-end gap-2">
                <span className="pb-1 font-display text-sm font-light tracking-wide text-muted-foreground">
                  ab
                </span>
                <span className="font-display text-5xl font-extralight leading-none tracking-tight text-foreground sm:text-6xl">
                  59&nbsp;€
                </span>
                <span className="pb-1.5 font-display text-sm font-light tracking-wide text-primary-glow">
                  netto / Monat
                </span>
              </div>

              <ParallelogramButton
                variant="primary"
                size="lg"
                className="font-display font-normal"
              >
                Kostenlosen Entwurf erhalten
                <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </ParallelogramButton>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
